import { useCallback } from 'react';
import { Cell, Rect } from '../types';

// Helper to normalize a selection range
const normalize = (start: Cell, end: Cell, colOrder: string[]): Rect => {
  const c0 = Math.min(colOrder.indexOf(start.colKey), colOrder.indexOf(end.colKey));
  const c1 = Math.max(colOrder.indexOf(start.colKey), colOrder.indexOf(end.colKey));
  const r0 = Math.min(start.row, end.row);
  const r1 = Math.max(start.row, end.row);
  return { r0, c0, r1, c1, colOrder };
};

interface UseTableKeyboardOptions {
  // State
  activeCell: Cell | null;
  anchorCell: Cell | null;
  selectionRect: Rect | null;
  colOrder: string[];
  rowCount: number;

  // State setters
  setActiveCell: (cell: Cell | null) => void;
  setAnchorCell: (cell: Cell | null) => void;
  setSelectionRect: (rect: Rect | null) => void;
  setShouldStartEditing: (editing: { row: number; colKey: string; initialChar?: string } | null) => void;

  // Actions
  copy: () => void;
  paste: (text: string) => void;
  performUndo: () => void;
}

export function useTableKeyboard({
  activeCell,
  anchorCell,
  selectionRect,
  colOrder,
  rowCount,
  setActiveCell,
  setAnchorCell,
  setSelectionRect,
  setShouldStartEditing,
  copy,
  paste,
  performUndo,
}: UseTableKeyboardOptions) {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Don't interfere with copy/paste when editing in an input field
      const target = e.target as HTMLElement;
      const isInInput =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // If in an input field and it's a copy/paste command, let the browser handle it natively
      if (isInInput && (e.metaKey || e.ctrlKey) && ['c', 'v', 'x', 'a'].includes(e.key)) {
        return;
      }

      // Check if it's a printable character that should start editing
      const isPrintable = !e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1;

      if (isPrintable && activeCell && !isInInput) {
        e.preventDefault();

        // Check if we have a multi-cell selection
        const hasMultipleCells =
          selectionRect &&
          (selectionRect.r0 !== selectionRect.r1 || selectionRect.c0 !== selectionRect.c1);

        if (hasMultipleCells && selectionRect) {
          // Update all selected cells with the typed character
          paste(e.key);
        } else {
          // Single cell - start editing mode
          setShouldStartEditing({ ...activeCell, initialChar: e.key });
        }
        return;
      }

      // Keyboard navigation
      if (!activeCell) return;

      const currentColIndex = colOrder.indexOf(activeCell.colKey);
      const currentRow = activeCell.row;

      // Arrow keys - with Shift modifier for range selection
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentRow > 0) {
          const newCell = { row: currentRow - 1, colKey: activeCell.colKey };
          setActiveCell(newCell);
          if (e.shiftKey) {
            const anchor = anchorCell || activeCell;
            setSelectionRect(normalize(anchor, newCell, colOrder));
            if (!anchorCell) setAnchorCell(activeCell);
          } else {
            setSelectionRect(normalize(newCell, newCell, colOrder));
            setAnchorCell(newCell);
          }
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentRow < rowCount - 1) {
          const newCell = { row: currentRow + 1, colKey: activeCell.colKey };
          setActiveCell(newCell);
          if (e.shiftKey) {
            const anchor = anchorCell || activeCell;
            setSelectionRect(normalize(anchor, newCell, colOrder));
            if (!anchorCell) setAnchorCell(activeCell);
          } else {
            setSelectionRect(normalize(newCell, newCell, colOrder));
            setAnchorCell(newCell);
          }
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentColIndex > 0) {
          const newCell = { row: currentRow, colKey: colOrder[currentColIndex - 1] };
          setActiveCell(newCell);
          if (e.shiftKey) {
            const anchor = anchorCell || activeCell;
            setSelectionRect(normalize(anchor, newCell, colOrder));
            if (!anchorCell) setAnchorCell(activeCell);
          } else {
            setSelectionRect(normalize(newCell, newCell, colOrder));
            setAnchorCell(newCell);
          }
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentColIndex < colOrder.length - 1) {
          const newCell = { row: currentRow, colKey: colOrder[currentColIndex + 1] };
          setActiveCell(newCell);
          if (e.shiftKey) {
            const anchor = anchorCell || activeCell;
            setSelectionRect(normalize(anchor, newCell, colOrder));
            if (!anchorCell) setAnchorCell(activeCell);
          } else {
            setSelectionRect(normalize(newCell, newCell, colOrder));
            setAnchorCell(newCell);
          }
        }
      }

      // Tab navigation (always moves to single cell, resets anchor)
      if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) {
          // Shift+Tab: previous cell
          if (currentColIndex > 0) {
            const newCell = { row: currentRow, colKey: colOrder[currentColIndex - 1] };
            setActiveCell(newCell);
            setAnchorCell(newCell);
            setSelectionRect(normalize(newCell, newCell, colOrder));
          } else if (currentRow > 0) {
            const newCell = { row: currentRow - 1, colKey: colOrder[colOrder.length - 1] };
            setActiveCell(newCell);
            setAnchorCell(newCell);
            setSelectionRect(normalize(newCell, newCell, colOrder));
          }
        } else {
          // Tab: next cell
          if (currentColIndex < colOrder.length - 1) {
            const newCell = { row: currentRow, colKey: colOrder[currentColIndex + 1] };
            setActiveCell(newCell);
            setAnchorCell(newCell);
            setSelectionRect(normalize(newCell, newCell, colOrder));
          } else if (currentRow < rowCount - 1) {
            const newCell = { row: currentRow + 1, colKey: colOrder[0] };
            setActiveCell(newCell);
            setAnchorCell(newCell);
            setSelectionRect(normalize(newCell, newCell, colOrder));
          }
        }
      }

      // Enter: move down (resets anchor)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (currentRow < rowCount - 1) {
          const newCell = { row: currentRow + 1, colKey: activeCell.colKey };
          setActiveCell(newCell);
          setAnchorCell(newCell);
          setSelectionRect(normalize(newCell, newCell, colOrder));
        }
      }

      // Cmd/Ctrl+A: select all (only if not in input field)
      if ((e.metaKey || e.ctrlKey) && e.key === 'a' && !isInInput) {
        e.preventDefault();
        if (!selectionRect) {
          setSelectionRect({ r0: 0, c0: 0, r1: rowCount - 1, c1: colOrder.length - 1, colOrder });
        }
      }

      // Cmd/Ctrl+C: copy (only if not in input field)
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && !isInInput) {
        e.preventDefault();
        e.stopPropagation();
        copy();
      }

      // Cmd/Ctrl+V: paste - let the paste event handle it
      // Don't preventDefault here - the onPaste handler will catch it

      // Cmd/Ctrl+Z: undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        performUndo();
      }
    },
    [
      activeCell,
      anchorCell,
      colOrder,
      rowCount,
      selectionRect,
      setActiveCell,
      setAnchorCell,
      setSelectionRect,
      setShouldStartEditing,
      copy,
      paste,
      performUndo,
    ]
  );

  return { onKeyDown };
}

export default useTableKeyboard;
